import {
  AutoSave,
  Button,
  Card,
  closeCurrentDialog,
  DataStatus,
  Form,
  openDialog,
  showNotification,
} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {find, isNil, isNotNil} from 'ramda';

import {useLazyGetVehicleCustomerQuery, usePostVehicleCustomerMutation} from '@dms/api/customer';
import {
  useCreateServiceVehicleMutation,
  useGetCustomerInquiryQuery,
  useGetWorkshopInterestQuery,
  useLazyGetServiceVehicleQuery,
  useSelectWorkshopInterestVehicleMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  handleApiError,
  ServiceVehicleForm,
  ServiceVehicleFormMode,
  ServiceVehicleFormVariant,
  useWorkshopInterestEditPermissions,
} from '@dms/shared';

import {ApiError, getApiDateString, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useVehicleDetailsUpdate} from '../hooks/useVehicleDetailsUpdate';
import {useVehicleInformationUpdate} from '../hooks/useVehicleInformationUpdate';
import {VehicleDetailsFormType, VehicleInformationFormType} from '../types';
import {
  transformVehicleDetailsToForm,
  transformVehicleInformationToForm,
} from '../utils/transformCustomerInquiry';
import {CustomerRequests} from './CustomerRequests';
import {SelectedVehicle} from './SelectedVehicle';
import {VehicleDetailsForm} from './VehicleDetailsForm';
import {VehicleInformationForm} from './VehicleInformationForm';

interface CustomerInquiryProps extends RequiredTestIdProps {
  interestId: string;
}

export function CustomerInquiry(props: CustomerInquiryProps) {
  const {data: workshopInterest} = useGetWorkshopInterestQuery({id: props.interestId});

  const {
    data: customerInquiry,
    isLoading,
    isFetching,
    isError,
  } = useGetCustomerInquiryQuery({interestId: props.interestId}, {refetchOnMountOrArgChange: true});

  const {onVehicleDetailsUpdate} = useVehicleDetailsUpdate(props.interestId);
  const {onVehicleInformationUpdate} = useVehicleInformationUpdate(props.interestId);

  const [selectVehicle] = useSelectWorkshopInterestVehicleMutation();
  const [getServiceVehicle] = useLazyGetServiceVehicleQuery();
  const [createServiceVehicle] = useCreateServiceVehicleMutation();
  const [getVehicleCustomer] = useLazyGetVehicleCustomerQuery();
  const [postVehicleCustomer] = usePostVehicleCustomerMutation();
  const [selectingVehicleId, setSelectingVehicleId] = useState<string | null>(null);

  const {hasUpdateCustomerRequestPermission} = useWorkshopInterestEditPermissions({
    interestId: props.interestId,
  });

  const vehicleId = workshopInterest?.vehicleId;
  const isVehicleSelected = isNotNil(vehicleId);

  const isVehicleDetailsDisabled =
    isNotNil(selectingVehicleId) || !hasUpdateCustomerRequestPermission;

  const isVehicleInformationDisabled = !hasUpdateCustomerRequestPermission;

  const handleSelectVehicle = async (selectedVehicleId: string) => {
    setSelectingVehicleId(selectedVehicleId);

    // Ensure the vehicle has a service entity before selecting
    const isServiceVehicleReady = await getServiceVehicle({vehicleId: selectedVehicleId})
      .unwrap()
      .catch((error: Error & {status?: number}) => {
        if (error?.status === 404) {
          return null;
        }
        throw error;
      })
      .then(async (serviceVehicle) => {
        if (isNil(serviceVehicle)) {
          await createServiceVehicle({vehicleId: selectedVehicleId}).unwrap();
        }
        return true;
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        return false;
      });

    if (!isServiceVehicleReady) {
      setSelectingVehicleId(null);
      return;
    }

    selectVehicle({
      id: props.interestId,
      selectVehicleRequestBody: {vehicleId: selectedVehicleId},
    })
      .unwrap()
      .then(async () => {
        const customerId = workshopInterest?.customerId;

        if (isNotNil(customerId)) {
          const customers = await getVehicleCustomer({vehicleId: selectedVehicleId}).unwrap();

          const hasVehicleOperator = find(
            (vehicleCustomer) =>
              vehicleCustomer?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR',
            customers ?? []
          );

          if (isNil(hasVehicleOperator)) {
            await postVehicleCustomer({
              body: {
                vehicleId: selectedVehicleId,
                customerId,
                customerType: 'CORVEHICLECUSTOMERTYPE_OPERATOR',
                eventType: 'vehicle',
                eventId: selectedVehicleId,
                dateFrom: getApiDateString(new Date()),
              },
            }).unwrap();
          }
        }

        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
      })
      .catch(handleApiError)
      .finally(() => {
        setSelectingVehicleId(null);
      });
  };

  const handleCreateServiceVehicle = (formValues: VehicleDetailsFormType) => {
    openDialog(
      <ServiceVehicleForm
        mode={ServiceVehicleFormMode.NEW}
        variant={ServiceVehicleFormVariant.SIMPLE}
        extendedDefaultFormData={{
          vin: formValues.vin,
          registrationPlate: formValues.licensePlate,
          type: formValues.vehicleType,
          make: formValues.make ?? undefined,
          formMake: formValues.make ?? '',
          modelFamily: formValues.modelFamily ?? undefined,
          formModelFamily: formValues.modelFamily ?? '',
          trim: formValues.trim,
          variant: formValues.variant,
          firstRegistrationOn: formValues.firstRegistration,
          manufacturedOnYear: formValues.realProductionYear,
          manufacturedOnMonth: formValues.realProductionMonth,
        }}
        onSuccess={(newVehicleId: string) => {
          const customerId = workshopInterest?.customerId;

          const selectAndAssign = selectVehicle({
            id: props.interestId,
            selectVehicleRequestBody: {vehicleId: newVehicleId},
          })
            .unwrap()
            .then(() => {
              if (isNotNil(customerId)) {
                return postVehicleCustomer({
                  body: {
                    vehicleId: newVehicleId,
                    customerId,
                    customerType: 'CORVEHICLECUSTOMERTYPE_OPERATOR',
                    eventType: 'vehicle',
                    eventId: newVehicleId,
                    dateFrom: getApiDateString(new Date()),
                  },
                }).unwrap();
              }
            });

          selectAndAssign
            .then(() => {
              showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
              closeCurrentDialog();
            })
            .catch((error: ApiError) => {
              handleApiError(error);
              closeCurrentDialog();
            });
        }}
        onDiscard={closeCurrentDialog}
        data-testid={suffixTestId('createServiceVehicleForm', props)}
      />,
      {
        title: i18n.t('entity.vehicle.labels.createServiceVehicle'),
        withAdditionalFooter: true,
      }
    );
  };

  return (
    <Card
      title={i18n.t('entity.workshopInterest.labels.customerInquiry')}
      isExpandable
      data-testid={suffixTestId('customerInquiry', props)}
    >
      <DataStatus isLoading={isLoading || isFetching} isError={isError} minHeight={200}>
        <VStack spacing={4}>
          <Show when={!isVehicleSelected}>
            <Form<VehicleDetailsFormType>
              defaultValues={
                customerInquiry
                  ? transformVehicleDetailsToForm(customerInquiry.vehicleDetails)
                  : undefined
              }
              isDisabled={isVehicleDetailsDisabled}
            >
              {(control, formApi) => (
                <>
                  <AutoSave<VehicleDetailsFormType> onSave={onVehicleDetailsUpdate} />
                  <VStack spacing={4}>
                    <HStack justify="space-between" align="center">
                      <Text size="base" alternative>
                        {i18n.t('entity.vehicle.labels.vehicleDetails')}
                      </Text>
                      <Button
                        title={i18n.t('entity.vehicle.labels.createServiceVehicle')}
                        leftIcon="maps/directions_car"
                        variant="link"
                        size="small"
                        isDisabled={!hasUpdateCustomerRequestPermission}
                        onClick={() => handleCreateServiceVehicle(formApi.getValues())}
                        data-testid={suffixTestId('createServiceVehicle', props)}
                      />
                    </HStack>
                    <VehicleDetailsForm
                      control={control}
                      formApi={formApi}
                      onVehicleSelect={handleSelectVehicle}
                      selectingVehicleId={selectingVehicleId}
                      data-testid={suffixTestId('vehicleDetails', props)}
                    />
                  </VStack>
                </>
              )}
            </Form>
          </Show>

          <Show when={isVehicleSelected}>
            <VStack spacing={4}>
              <Text size="base" alternative>
                {i18n.t('entity.vehicle.labels.vehicleDetails')}
              </Text>
              <SelectedVehicle
                vehicleId={vehicleId ?? ''}
                interestId={props.interestId}
                data-testid={suffixTestId('selectedVehicle', props)}
              />
            </VStack>
          </Show>

          <Form<VehicleInformationFormType>
            defaultValues={
              customerInquiry
                ? transformVehicleInformationToForm(customerInquiry.vehicleInformation)
                : undefined
            }
            isDisabled={isVehicleInformationDisabled}
          >
            {(control) => (
              <>
                <AutoSave<VehicleInformationFormType> onSave={onVehicleInformationUpdate} />
                <VehicleInformationForm
                  control={control}
                  data-testid={suffixTestId('vehicleInformation', props)}
                />
              </>
            )}
          </Form>

          <CustomerRequests
            interestId={props.interestId}
            customerRequests={customerInquiry?.customerRequests ?? []}
            isDisabled={!hasUpdateCustomerRequestPermission}
            data-testid={suffixTestId('customerRequests', props)}
          />
        </VStack>
      </DataStatus>
    </Card>
  );
}
